import React, { Suspense, useEffect } from 'react'
import { Route, Routes } from 'react-router-dom'
import { IISMethods, Config, socket, apisocket } from './config/IISMethods'
import { ToastContainer } from 'react-toastify'
import LogoLoader from './view/Components/LogoLoader'
import SignIn from './Controller/SignIn'
import AuthCallback from './view/Integration/AuthCallback'
import { useSocket } from './view/CustomeHooks/useSocket'
import { initErrorHandling, cleanupErrorHandling } from './view/Components/ErrorManager'

const SubmitSurvey = React.lazy(() => lazyRetry(() => import('./view/Configurations/SubmitSurvey')))
const SubmitBackgroundVerificationSurvey = React.lazy(() => lazyRetry(() => import('./view/Recruitment Master/Application Modal Features/SubmitBackgroundVerificationSurvey')))
const Home = React.lazy(() => lazyRetry(() => import('./Home')))
const Configure = React.lazy(() => lazyRetry(() => import('./InitialInfo/Configure')))
const OnlineApptitudeTest = React.lazy(() => lazyRetry(() => import('./view/Recruitment Master/OnlineApptitudeTest')))
const SubDomainPage = React.lazy(() => lazyRetry(() => import('./view/subDomainPage')))
const SignUp = React.lazy(() => lazyRetry(() => import('./view/SignUp')))
const SignupRequest = React.lazy(() => lazyRetry(() => import('./view/SignupRequest')))
const PlacementDriveApptitudeTest = React.lazy(() => lazyRetry(() => import('./view/Recruitment Master/PlacementDriveApptitudeTest')))
const CareerOpenings = React.lazy(() => lazyRetry(() => import('./view/Recruitment Master/CareerOpenings')))
const CRMLeadEmbed = React.lazy(() => lazyRetry(() => import('./view/CRM/CRMLeadEmbed')))
const Booking = React.lazy(() => lazyRetry(() => import('./view/Booking/BookingConfiguration/Booking')))
// const SessionTimeoutLogin = React.lazy(() => lazyRetry(() => import('./view/Components/SessionTimeoutLogin')))
// const CommonDesignTemplete = React.lazy(() => lazyRetry(() => import('../src/CommonDesignTemplete')))
const Emp360FilePreview = React.lazy(() => lazyRetry(() => import('./view/Configurations/Emp360FilePreview')))
const UploadDocumentsScreen = React.lazy(() => lazyRetry(() => import('./view/Recruitment Master/UploadDocumentsScreen')))
const LetterAcknowledgementLoginScreen = React.lazy(() => lazyRetry(() => import('./view/Recruitment Master/LetterAcknowledgementLoginScreen')))

// Utsav ~ Error fetching
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    // Store error info for display
    this.setState({
      error,
      errorInfo
    })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className='d-flex flex-column align-items-center justify-content-center p-12 text-center minh-300p' >
          <h2>Something went wrong.</h2>
          <p>We've been notified and are working on it.</p>
          <button
            className='btn btn-primary-dark'
            onClick={() => window.location.reload()}
          >
            {Config.reloadpagebtn}
          </button>
        </div>
      )
    }
    return this.props.children
  }
}


// a function to retry loading a chunk to avoid chunk load error for out of date code
function lazyRetry(componentImport) {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      // window.sessionStorage.getItem('retry-lazy-refreshed') || 'false'
      Object.keys(IISMethods.getSessionData('retry-lazy-refreshed') || 'false')?.length ? IISMethods.getSessionData('retry-lazy-refreshed') : 'false'

    )

    // try to import the component
    componentImport().then((component) => {
      // window.sessionStorage.setItem('retry-lazy-refreshed', 'false') // success so reset the refresh
      IISMethods.setSessionData('retry-lazy-refreshed', 'false') // success so reset the refresh
      resolve(component)
    }).catch((error) => {
      if (!hasRefreshed) { // not been refreshed yet
        // window.sessionStorage.setItem('retry-lazy-refreshed', 'true') // we are now going to refresh
        IISMethods.setSessionData('retry-lazy-refreshed', 'true') // we are now going to refresh
        return window.location.reload() // refresh the page
      }
      reject(error) // Default error behaviour as already tried refresh
    })
  })
}

export default function Router() {
  const { socketEmit } = useSocket()

  //PRIYANSHI --> SOCKET
  const checkAndReconnectSockets = () => {
    if (!socket.connected) {
      console.warn('Main socket disconnected on focus. Reconnecting...')
      socket.disconnect()
      socket.connect()
    }
    if (!apisocket.connected) {
      console.warn('API socket disconnected on focus. Reconnecting...')
      apisocket.disconnect()
      apisocket.connect()
    }
  }

  useEffect(() => {
    if (Config.enableErrorTracking) initErrorHandling()

    // Utsav ~ Error fetching
    // Cleanup when component unmounts
    return () => {
      cleanupErrorHandling()
    }
  }, [])

  useEffect(() => {
    async function fetchData() {

      socket.on('connect', async () => {
        console.error('reconnected to server')
        let sessionData = IISMethods.getSessionData("sessionData") || {}
        let loginInfo = IISMethods.getCookiesData("loginInfo")
        if (loginInfo) {
          sessionData.subscribedSocket?.map((socketdata) => {
            socketEmit(socketdata.key, socketdata.value)
          })
        }
      })

      apisocket.on('connect', async () => {
        console.error('reconnected to api server')
        // if (Config.useSocketForAPI) {
        //   Config.callSocketApi = true
        // }
      })
      window.addEventListener('focus', checkAndReconnectSockets)

      const socketWatcher = setInterval(() => {
        if (!socket.connected) {
          console.warn('Main socket disconnected. Attempting to reconnect...')
          socket.disconnect()
          socket.connect()
          // attemptReconnect() 
        }

        if (!apisocket.connected) {
          console.warn('API socket disconnected. Attempting to reconnect...')
          apisocket.disconnect()
          apisocket.connect()
          // attemptAPISocketReconnect() 
        }
      }, 3000)

      return () => {
        clearInterval(socketWatcher)
        window.removeEventListener('focus', checkAndReconnectSockets)
      }

    }

    fetchData()

  }, [])

  // Oauth callback for integration | Aniket Bhesaniya
  if (window.location.href?.includes(`${Config.redirectionurl}`)) {
      return <>
      <AuthCallback />
      </>
    
  }
  const pagename = IISMethods.getpagename()
  const pageUrlParams = IISMethods.getPageUrlParams()
  let windowsUrl = window.location.href
  let exitPersonId = ""
  let exitApplicationId = ""
  let exitstageId = ""
  let surveyId = ""
  let clientId = ""
  let projectId = ""
  let applicationid = ''
  let surveyformid = ''
  const handleDecryptUrlId = (id) => {
    let decryptedsurveyId
    try {
      if (id) {
        let decodedsurveyId = decodeURIComponent(id)
        let isDecrypted = IISMethods.isEncryptedId(decodedsurveyId)
        decryptedsurveyId = (!isDecrypted) ? IISMethods.decryptData(decodedsurveyId, true) : ""
      }
      else {
      }
    } catch (error) {
      decryptedsurveyId = ""
    }
    return decryptedsurveyId?.replace(/^"|"$/g, '')?.length ? decryptedsurveyId?.replace(/^"|"$/g, '') : id
  }

  //  | React 18 Changes Om Z  🌝🌝🌝
  const getStaticSegmentCount = (pathSegments) => {
    let newcount = 0;
    for (let count = 1; count <= pathSegments?.length; count++) {
      const potentialRoute = pathSegments?.slice(0, count).join('/');
      if (["submitsurvey", 'backgroundverificationsurvey']?.includes(potentialRoute)) {
        newcount = count
      }
    }
    return newcount;
  }


  //  | React 18 Changes Om Z  🌝🌝🌝
  const parseNewURL = () => {
    const parsedUrl = window.location.pathname
    const pathSegments = parsedUrl.split('/')?.filter(segment => segment?.length > 0);
    const staticSegmentCount = getStaticSegmentCount(pathSegments);
    if (staticSegmentCount > pathSegments?.length) {
      return [pathSegments.join('/')];
    }
    const remainingPart = pathSegments?.slice(staticSegmentCount)
    return [...remainingPart]
  }

  // changes of splitUrl and pagename conditions | React 18 Changes Om Z  🌝🌝🌝
  if (pageUrlParams[1] == 'submitsurvey') {
    const params = new URLSearchParams(window.location.search)
    // if form is for employee then its goes in sidebarrouter and login it which user form for and same condition applied if form type is survey and client feedback  | Om Z  🌝🌝🌝 ~ 2024-07-10 ~ 12:32 PM
    // if form is for customer then its goes in router and form open without login and same condition applied if form type is survey and client feedback  | Om Z  🌝🌝🌝 ~ 2024-07-10 ~ 12:32 PM
    const [id, eid, eaid, sid] = parseNewURL()
    let ExitsurveyId = params.get('id') ? handleDecryptUrlId(params.get('id')) : handleDecryptUrlId(id)
    let ExitpersonId = params.get('eid') ? handleDecryptUrlId(params.get('eid')) : handleDecryptUrlId(eid)
    let ExitapplicationId = params.get('eaid') ? handleDecryptUrlId(params.get('eaid')) : handleDecryptUrlId(eaid)
    let ExitstageId = params.get('sid') ? handleDecryptUrlId(params.get('sid')) : handleDecryptUrlId(sid)

    if (ExitpersonId && ExitapplicationId && ExitstageId && ExitsurveyId) {
      exitPersonId = ExitpersonId
      exitApplicationId = ExitapplicationId
      exitstageId = ExitstageId
    }
    // if don't get exitapplicationid
    else {
      const [id, cid, pid] = parseNewURL()
      surveyId = handleDecryptUrlId(params.get('id')) ? handleDecryptUrlId(params.get('id')) : handleDecryptUrlId(id)
      clientId = handleDecryptUrlId(params.get('cid')) ? handleDecryptUrlId(params.get('cid')) : handleDecryptUrlId(cid)
      projectId = handleDecryptUrlId(params.get('pid')) ? handleDecryptUrlId(params.get('pid')) : handleDecryptUrlId(pid)
    }
  }
  else if (pageUrlParams[1] == 'backgroundverificationsurvey') {
    // splitUrl = windowsUrl?.split("/")
    let [sid, aid] = parseNewURL()
    // Create a URL object
    const url = new URL(windowsUrl);
    // Use URLSearchParams to get the query parameters
    const params = new URLSearchParams(url.search);

    surveyformid = params.get('surveyformid') ? handleDecryptUrlId(params.get('surveyformid')) : handleDecryptUrlId(sid)
    applicationid = params.get('applicationid') ? handleDecryptUrlId(params.get('applicationid')) : handleDecryptUrlId(aid)
  }

  const getUrlId = (value) => {
    var urlpath = window.location.pathname
    var pagenamepath = urlpath.split("/")
    var pagename = pagenamepath[value]
    return pagename
  }


  return (
    <>
      {/* <ErrorBoundary> */}
        <Routes>
          {
            (pageUrlParams[1] === 'placements') ?
              (
                <>
                  {
                    ["/placements", "/placements/:pid/:id", "/placements/:pid"]?.map((path) => {
                      return <Route path={path} key={path?.length} element={<Suspense fallback={<LogoLoader />}><PlacementDriveApptitudeTest /></Suspense>} />
                    })
                  }
                </>
              )
              // :
              // (pageUrlParams[1] === 'commondesignyemplete') ?
              //   (
              //     <>
              //       <Route exact path={`/${'commondesignyemplete'}`} element={<Suspense fallback={<LogoLoader />}><CommonDesignTemplete /></Suspense>} />

              //     </>
              //   )
                :
                (pageUrlParams[1] === Config.aliasname['leadembedformalias']) ?
                  (
                    <>
                      {
                        [`/${Config.aliasname['leadembedformalias']}`, `/${Config.aliasname['leadembedformalias']}/:companyid`]?.map((path) => {
                          return <Route path={path} element={<Suspense fallback={<LogoLoader />}><CRMLeadEmbed pagealias={Config.aliasname['leadembedformalias']} pagename={Config.pagenamelist['leadembedform']} /></Suspense>} />
                        })
                      }
                    </>
                  )
                  :

                  pageUrlParams[1] === 'careers' ?
                    // Add extra routepaths for copy link | Om Z  🌝🌝🌝 ~ 2024-06-28 ~ 04:30 PM
                    (
                      <>
                        {
                          // id --openingid | pid --personid | type -- 1=allopening, 2=singleopening
                          ['/careers', "/careers/:id/:type", '/careers/:id/:type/:companyid', '/careers/:id/:type/:companyid/:pid']?.map((path) => {
                            return <Route path={path} element={<Suspense fallback={<LogoLoader />}><CareerOpenings /></Suspense>} />
                          })
                        }
                      </>
                    )
                    :

                    //  | JATIN 🐒 ~  2024-07-01 ~ 09:39 AM
                    pageUrlParams[1] === 'onlineapptitudetest' ?
                      <>
                        {
                          ["/onlineapptitudetest/:testid/:appid", "/onlineapptitudetest"]?.map(path => {
                            return (
                              <Route key={path} path={path} element={<Suspense fallback={<LogoLoader />}><OnlineApptitudeTest /></Suspense>} />
                            )
                          })
                        }
                      </>
                      :
                      // upload document | Nirzar Gabani 💀 ~  2025-05-09 ~ 12:39 PM
                      pageUrlParams[1] === Config.aliasname['uploaddocumentsalias'] ?
                        <>
                          {
                            [`/${Config.aliasname['uploaddocumentsalias']}/:applicationid/:joiningstageid/:employeeorintern`, `/${Config.aliasname['uploaddocumentsalias']}`]?.map(path => {
                              return (
                                <Route key={path} path={path} element={<Suspense fallback={<LogoLoader />}><UploadDocumentsScreen /></Suspense>} />
                              )
                            })
                          }
                        </>
                        :
                        // upload document | Nirzar Gabani 💀 ~  2025-05-09 ~ 12:39 PM
                        pageUrlParams[1] === Config.aliasname['letteracknowledgementalias'] ?
                          <>
                            {
                              [`/${Config.aliasname['letteracknowledgementalias']}/:applicationid/:joiningstageid/:employeeorintern/:lettertype`, `/${Config.aliasname['letteracknowledgementalias']}/:applicationid/:joiningstageid/:employeeorintern/:documentid`, `/${Config.aliasname['letteracknowledgementalias']}`]?.map(path => {
                                return (
                                  <Route key={path} path={path} element={<Suspense fallback={<LogoLoader />}><LetterAcknowledgementLoginScreen /></Suspense>} />
                                )
                              })
                            }
                          </>
                          :
                          pageUrlParams[1] === 'submitsurvey' && exitApplicationId ?
                            // add map for routepaths for queryparams and routing | Om Z  🌝🌝🌝 ~ 2024-06-28 ~ 04:30 PM
                            <>
                              {
                                ['/submitsurvey', '/submitsurvey/:id/:eid/:eaid/:sid']?.map(path => {
                                  return <Route path={path} element={<Suspense fallback={<LogoLoader />}><SubmitSurvey pagename={"getsurvey"} exitApplicationId={exitApplicationId} exitPersonId={exitPersonId} exitstageId={exitstageId} /> </Suspense>} />
                                })

                              }
                            </>
                            :
                            // Utsav
                            pageUrlParams[1] === Config.pagenamelist['shareroute'] && IISMethods.getCookiesData("loginInfo") ?
                              (
                                <>
                                  {
                                    [
                                      `/${Config.pagenamelist['shareroute']}/:user/:fileId`,
                                    ]?.map((path) => {
                                      return <Route path={path} element={<Suspense fallback={<LogoLoader />}><Emp360FilePreview /></Suspense>} />
                                    })
                                  }
                                </>
                              )
                              :

                              //  | React 18 Changes Om Z  🌝🌝🌝
                              pageUrlParams[1] === 'submitsurvey' && clientId && projectId ?
                                <>
                                  {
                                    ["/submitsurvey", "/submitsurvey/:id", "/submitsurvey/:id/:cid/:pid"]?.map(path => {
                                      return <Route key={path} path={path} element={<Suspense fallback={<LogoLoader />}> <SubmitSurvey pagename={"getsurvey"} surveyId={surveyId} clientId={clientId} projectId={projectId} /> </Suspense>} />
                                    })
                                  }
                                </>
                                :
                                pageUrlParams[1] === 'booking' ?
                                  <>
                                    {
                                      pageUrlParams[2] === 'workspace' ?
                                        <>
                                          {
                                            ["/booking/workspace/:id"]?.map(path => {
                                              return <Route key={path} path={path} element={<Suspense fallback={<LogoLoader />}> <Booking pagename={"workspace"} bookingid={handleDecryptUrlId(getUrlId(3))} /> </Suspense>} />
                                            })
                                          }
                                        </>
                                        :
                                        pageUrlParams[2] === 'consultation' ?
                                          <>
                                            {
                                              ["/booking/consultation/:id", "/booking/consultation/:id/:aid"]?.map(path => {
                                                return <Route key={path} path={path} element={<Suspense fallback={<LogoLoader />}> <Booking pagename={"consultation"} bookingid={handleDecryptUrlId(getUrlId(3))} appointmentid={handleDecryptUrlId(getUrlId(4))} /> </Suspense>} />
                                              })
                                            }
                                          </>
                                          :
                                          pageUrlParams[2] === 'consultant' ?
                                            <>
                                              {
                                                ["/booking/consultant/:id"]?.map(path => {
                                                  return <Route key={path} path={path} element={<Suspense fallback={<LogoLoader />}> <Booking pagename={"consultant"} bookingid={handleDecryptUrlId(getUrlId(3))} /> </Suspense>} />
                                                })
                                              }
                                            </>
                                            :
                                            <></>
                                    }
                                  </>
                                  :
                                  // add the condition of pageUrlParams | Om Z  🌝🌝🌝 ~ 2024-06-28 ~ 06:15 PM
                                  pageUrlParams[1] === 'backgroundverificationsurvey' && applicationid && surveyformid
                                    ?
                                    <>
                                      {
                                        ["/backgroundverificationsurvey", "/backgroundverificationsurvey/:surveyformid/:applicationid"]?.map((path) => {
                                          return <Route path={path} element={<Suspense fallback={<LogoLoader />}><SubmitBackgroundVerificationSurvey pagename={"applicationsurveyform"} applicationid={applicationid} surveyformid={surveyformid} /></Suspense>} />
                                        })
                                      }
                                    </>
                                    :
                                    ((IISMethods.getSubdomainName() && !IISMethods.getCookiesData("loginInfo")) || (pagename === 'login')) ?
                                      <Route path="/*" element={<SignIn />} />
                                      :
                                      pagename === 'signup' ?
                                        <Route path="/signup" element={<Suspense fallback={<LogoLoader />}> <SignUp /></Suspense>} />
                                        :
                                        pageUrlParams[1] === 'signup' ?
                                          <>
                                            {
                                              ["/signup/:id", "/signup/"]?.map((path) => {
                                                return (
                                                  <Route path={path} element={<Suspense fallback={<LogoLoader />}> <SignUp /></Suspense>} />
                                                )
                                              })
                                            }
                                          </>
                                          :

                                          (!IISMethods.getSubdomainName() && !IISMethods.getCookiesData("loginInfo") &&
                                            pagename === '') || (!IISMethods.getSubdomainName() && !IISMethods.getCookiesData("loginInfo")) ?
                                            <Route path="/*" element={<Suspense fallback={<LogoLoader />}><SubDomainPage /></Suspense>} />
                                            :
                                            pagename === 'signuprequest' ?
                                              <Route path="/signuprequest" element={<Suspense fallback={<LogoLoader />}> <SignupRequest /></Suspense>} />

                                              :
                                              pagename === "initialinfo" ?
                                                <Route path="/initialinfo" element={<Suspense fallback={<LogoLoader />}><Configure /></Suspense>} />
                                                :
                                                IISMethods.getCookiesData('loginInfo') && !IISMethods.getCookiesData('loginInfo')?.status ?
                                                  <Route path="/*" element={<Suspense fallback={<LogoLoader />}> <Home /> </Suspense>} />
                                                  :
                                                  <></>


          }
        </Routes>
      {/* </ErrorBoundary> */}

      {/* react tostify  */}
      <ToastContainer
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        // closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  )
}

// without lazy laoding....................
